import React, { useEffect, useMemo, useState } from 'react'
import { Autocomplete, Box, Button, Chip, MenuItem, Pagination, TextField } from '@mui/material'
import IntelLayout from './components/IntelLayout'
import Badge from './components/Badge'
import IntelTable from './components/IntelTable'
import CopyButton from './components/CopyButton'
import { intelColors, formatNumber } from './components/intelTheme'
import { intelApi } from './api'
import ResolutionPlan from './ResolutionPlan'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls'

const ZONES = ['', 'ROI', 'LOCAL', 'REGIONAL', 'METROS']
const PAYMENTS = ['', 'COD', 'PREPAID']
const PAGE_SIZE = 10
const inr = (v) => '₹' + formatNumber(Math.round(v || 0))
const EMPTY = { merchant: null, zone: '', paymentMode: '', category: '', date: '' }

export default function StuckOrders() {
  const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const [view, setView] = useState('orders')      // 'orders' | 'resolution' (chip sub-nav)
  const [draft, setDraft] = useState(EMPTY)       // edited in the filter bar
  const [applied, setApplied] = useState(EMPTY)   // committed on Apply → drives the fetch
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [data, setData] = useState(null)

  const merchantOptions = useMemo(
    () => (merchants || []).filter((m) => m?.merchantId).map((m) => ({ value: String(m.merchantId), label: m.name || String(m.merchantId) })),
    [merchants]
  )
  const merchantName = useMemo(() => {
    const map = {}
    ;(merchants || []).forEach((m) => { if (m?.merchantId) map[String(m.merchantId)] = m.name })
    return map
  }, [merchants])

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    intelApi
      .stuckOrders({
        merchant: applied.merchant?.value,
        zone: applied.zone,
        paymentMode: applied.paymentMode,
        category: applied.category,
        date: applied.date,
        page,
        size: PAGE_SIZE,
      })
      .then((res) => {
        if (cancel) return
        setData(res?.data?.responseObj || res?.data?.data || res?.data)
      })
      .catch((e) => !cancel && setErr(e?.response?.data?.exception || e.message))
      .finally(() => !cancel && setLoading(false))
    return () => {
      cancel = true
    }
  }, [applied, page])

  const rows = data?.rows || []
  const cats = data?.categoryCounts || []
  const totalRecords = data?.totalRecords || 0
  const totalPages = data?.totalPages || 0

  const setF = (k, v) => setDraft((d) => ({ ...d, [k]: v }))
  const apply = () => { setApplied(draft); setPage(1) }
  const reset = () => { setDraft(EMPTY); setApplied(EMPTY); setPage(1) }

  const columns = [
    { key: 'ref', header: 'Order', render: (r) => <code style={{ fontSize: 11 }}>{r.reference || r.orderId || '—'}</code> },
    { key: 'merchant', header: 'Merchant', render: (r) => merchantName[r.merchant] || r.merchant || '—' },
    { key: 'gmv', header: 'GMV', render: (r) => inr(r.gmv) },
    { key: 'zone', header: 'Zone', render: (r) => (r.zone ? <Badge color='grey'>{r.zone}</Badge> : '—') },
    { key: 'paymentMode', header: 'MOP', render: (r) => <Badge color={r.paymentMode === 'COD' ? 'orange' : 'green'}>{r.paymentMode || '—'}</Badge> },
    {
      key: 'remark',
      header: 'Remark (ProShip)',
      cellStyle: { maxWidth: 320, fontSize: 11 },
      render: (r) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', maxWidth: 300 }}>
          <Box title={r.remark || ''} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: '1 1 auto' }}>{r.remark || '—'}</Box>
          <CopyButton text={r.remark} title='Copy full remark' />
        </Box>
      ),
    },
    { key: 'category', header: 'Classification', render: (r) => <Badge color='red'>{r.categoryLabel || r.category || '—'}</Badge> },
  ]

  return (
    <IntelLayout title='Stuck Orders — Unprocessed Today'>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Chip label='Orders' color='primary' variant={view === 'orders' ? 'filled' : 'outlined'} onClick={() => setView('orders')} />
        <Chip label='Resolution Plan' color='primary' variant={view === 'resolution' ? 'filled' : 'outlined'} onClick={() => setView('resolution')} />
      </Box>

      {view === 'resolution' ? (
        <ResolutionPlan merchantName={merchantName} />
      ) : (
        <>
      <Box sx={{ background: '#fff', border: `1px solid ${intelColors.border}`, borderRadius: '8px', p: '14px 18px', mb: 2, display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {/* No date filter: this is the live "today" backlog. Older still-stuck orders are archived and
            surfaced on Non-serviceable / Stuck Trends, not here. */}
        <Autocomplete
          options={merchantOptions}
          getOptionLabel={(o) => o.label || ''}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          value={draft.merchant}
          onChange={(_, v) => setF('merchant', v)}
          size='small'
          sx={{ minWidth: 220 }}
          renderInput={(params) => <TextField {...params} label='Merchant' placeholder='Search…' />}
        />
        <TextField select label='Zone' value={draft.zone} onChange={(e) => setF('zone', e.target.value)} size='small' sx={{ minWidth: 130 }}>
          {ZONES.map((z) => <MenuItem key={z || 'all'} value={z}>{z || 'All'}</MenuItem>)}
        </TextField>
        <TextField select label='Payment' value={draft.paymentMode} onChange={(e) => setF('paymentMode', e.target.value)} size='small' sx={{ minWidth: 120 }}>
          {PAYMENTS.map((p) => <MenuItem key={p || 'all'} value={p}>{p || 'All'}</MenuItem>)}
        </TextField>
        <TextField select label='Category' value={draft.category} onChange={(e) => setF('category', e.target.value)} size='small' sx={{ minWidth: 220 }}>
          <MenuItem value=''>All</MenuItem>
          {cats.map((c) => <MenuItem key={c.code} value={c.code}>{`${c.label} (${c.count})`}</MenuItem>)}
        </TextField>
        <Button variant='contained' onClick={apply}>Apply</Button>
        <Button variant='outlined' color='error' onClick={reset}>Reset</Button>
      </Box>

      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>{err}</Box>
      )}

      <IntelTable
        title='Unprocessed Orders — Today'
        action={<Badge color='red'>{`${formatNumber(totalRecords)} orders`}</Badge>}
        loading={loading}
        columns={columns}
        rows={rows}
        rowKey={(r, i) => r.reference || r.orderId || i}
        emptyMsg='No stuck orders (UNPROCESSED) for this filter'
      />

      {totalRecords > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ fontSize: 11.5, color: intelColors.muted }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalRecords)} of {formatNumber(totalRecords)}
          </Box>
          <Pagination count={totalPages || 1} page={page} onChange={(_, p) => setPage(p)} size='small' color='primary' />
        </Box>
      )}
        </>
      )}
    </IntelLayout>
  )
}
